/**
 * Topbar — v3 (Spec §2.2, Mai 2026).
 *
 * Höhe: 52px, bg-bg-surface, border-bottom: 1px border-border-default.
 *
 * Layout (links → rechts):
 *   1. Mobile-Hamburger (md:hidden)
 *   2. Breadcrumbs (links, klickbar, Truncate-Middle bei >3 Levels)
 *   3. ⌘K-Pill (mittig via absolute, 320px, bg-bg-subtle, "Suchen oder Aktion ⌘K")
 *   4. Right-Cluster: Sandbox-Badge (non-prod) → Notifications-Bell → optional Avatar-Fallback
 *
 * WorkspaceSwitcher + UserMenu sitzen in der Sidebar — nicht hier.
 */
import { AlertTriangle, Bell, Menu, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCommandStore } from "@/stores/command";
import { useNotifications } from "@/hooks/use-notifications";
import { useSidebar } from "@/contexts/SidebarContext";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { KbdHint } from "@/components/shared";
import { formatRelative } from "@/lib/date";
import { cn } from "@/lib/utils";

const PROD_HOSTNAME = "paypilot.kalkuel.app";

function isSandboxHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  // Localhost zählt als Sandbox (Dev). Echtes Prod-Hostname = nicht Sandbox.
  return host !== PROD_HOSTNAME;
}

export function Topbar() {
  const setCmdOpen = useCommandStore((s) => s.setOpen);
  const { setOpen: setSidebarOpen } = useSidebar();
  const { data: notifications = [] } = useNotifications();
  const unread = notifications.filter((n) => !n.read).length;
  const isSandbox = isSandboxHost();

  return (
    <header
      role="banner"
      // Spec §2.2: Höhe 52px, bg-bg-surface, border-bottom: 1px border-border-default.
      // `relative` weil die ⌘K-Pill absolute-mittig positioniert ist.
      className="relative sticky top-0 z-30 flex h-[52px] shrink-0 items-center gap-3 border-b border-border-default bg-bg-surface px-4"
    >
      {/* Mobile-Hamburger */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={() => setSidebarOpen(true)}
        aria-label="Navigation öffnen"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Breadcrumbs (links) — flex-1 nimmt Restplatz, ⌘K-Pill liegt absolute mittig */}
      <div className="hidden min-w-0 flex-1 items-center md:flex">
        <Breadcrumbs />
      </div>

      {/* Spacer für mobile (Breadcrumbs hidden) */}
      <div className="flex-1 md:hidden" />

      {/* ⌘K-Pill — Spec §2.2: mittig, 320px, bg-bg-subtle Pill.
       * Absolute positioning damit er unabhängig von Breadcrumbs-Breite
       * exakt mittig sitzt. */}
      <button
        type="button"
        onClick={() => setCmdOpen(true)}
        aria-label="Suche öffnen oder Aktion ausführen"
        className={cn(
          "absolute left-1/2 top-1/2 hidden h-8 w-[320px] -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full bg-bg-subtle px-3",
          "text-small text-text-tertiary transition-colors duration-fast ease-out-quint",
          "hover:bg-bg-muted hover:text-text-secondary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface",
          "md:flex",
        )}
      >
        <Search className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 truncate text-left">Suchen oder Aktion ausführen</span>
        <KbdHint keys="cmd k" size="sm" />
      </button>

      {/* Right-Cluster */}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {isSandbox && (
          <>
            {/* XS: kompakter Dot-Indicator (immer sichtbar). */}
            <span
              role="status"
              aria-label="Sandbox-Umgebung — keine echten Zahlungen"
              title="Sandbox-Umgebung — keine echten Zahlungen"
              className="inline-flex h-2 w-2 shrink-0 rounded-full bg-status-pending-text sm:hidden"
            />
            {/* >=sm: volles Badge mit Label. */}
            <span
              role="status"
              className="hidden items-center gap-1 rounded-md bg-status-pending-bg px-2 py-0.5 text-tiny font-medium text-status-pending-text sm:inline-flex"
              title="Sandbox-Umgebung — keine echten Zahlungen"
            >
              <AlertTriangle className="h-3 w-3" />
              Sandbox
            </span>
          </>
        )}

        {/* Notifications-Bell (Spec §2.2 Right-Cluster) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8"
              aria-label={
                unread > 0
                  ? `Benachrichtigungen (${unread} ungelesen)`
                  : "Benachrichtigungen"
              }
            >
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span className="absolute right-1 top-1.5 flex h-2 w-2 items-center justify-center rounded-full bg-brand-600 ring-2 ring-bg-surface" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>Benachrichtigungen</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.length === 0 && (
              <div className="p-4 text-small text-text-tertiary">
                Keine neuen Meldungen.
              </div>
            )}
            {notifications.slice(0, 6).map((n) => (
              <DropdownMenuItem
                key={n.id}
                className="flex flex-col items-start gap-0.5"
              >
                <div className="flex w-full items-center gap-2">
                  <span className="font-medium">{n.title}</span>
                  <Badge
                    variant={
                      n.level === "urgent"
                        ? "fraud"
                        : n.level === "warning"
                          ? "warning"
                          : "info"
                    }
                    className="ml-auto text-tiny"
                  >
                    {n.level}
                  </Badge>
                </div>
                <span
                  className={cn(
                    "text-tiny text-text-tertiary",
                    !n.read && "text-text-primary",
                  )}
                >
                  {n.body}
                </span>
                <span className="text-tiny text-text-tertiary">
                  {formatRelative(n.createdAt)}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
